import React, { useState } from 'react';
import cn from 'classnames';
import { useFloating, autoUpdate, offset, flip, shift, arrow, useHover, useClick, useFocus, useDismiss, useRole, useInteractions, FloatingPortal, FloatingArrow } from '@floating-ui/react';

import defaultStyles from './Tooltip.module.postcss';

function Tooltip({ children, content, placement = 'top', lightTheme = false, hideArrow = false, triggerOnClick = false, onShow, onHide, parentClassName, offsetValue = 5, theme }) {
  const styles = theme || defaultStyles;

  const [isOpen, setIsOpen] = useState(false);
  const [arrowRef, setArrowRef] = useState(null);

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open && onShow) {
      onShow();
    } else if (!open && onHide) {
      onHide();
    }
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement,
    strategy: 'fixed',
    middleware: [offset(offsetValue), flip(), shift({ padding: 10 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    enabled: !triggerOnClick,
    delay: { open: 0, close: 200 },
    restMs: 40,
  });
  const click = useClick(context, { enabled: triggerOnClick });
  const focus = useFocus(context);
  // When using click trigger, don't dismiss when clicking inside the floating element
  const dismiss = useDismiss(context, { outsidePress: triggerOnClick ? true : undefined });
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    hover,
    focus,
    dismiss,
    role,
  ]);

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()} className={parentClassName}>
        {children}
      </div>

      <FloatingPortal>
        { isOpen &&
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={cn(styles.Tooltip, {
              [styles.lightTheme]: lightTheme,
            })}
          >
            {content}

            { !hideArrow &&
              <FloatingArrow ref={setArrowRef} context={context} fill={lightTheme ? 'white' : '#333'} />
            }
          </div>
        }
      </FloatingPortal>
    </>
  );
}

export default Tooltip;
